import { useEffect, useRef } from 'react';
import { COMPANY, addressLine } from './legal.js';
import { legalDocument } from './legal-translations.js';
import type { Lang } from './i18n.js';

/**
 * Terms and privacy in a dialog rather than a page.
 *
 * A customer reading the terms has not abandoned the payment - sending them to
 * another page means losing the form they filled in. A dialog keeps the
 * checkout underneath, so closing it puts them exactly where they were.
 *
 * Uses the native <dialog> element: it gives focus trapping, Escape to close
 * and inertness of the page behind it without any of that being reimplemented
 * badly.
 */
export function LegalModal({
  doc,
  lang,
  onClose,
}: {
  doc: 'terms' | 'privacy';
  lang: Lang;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const content = legalDocument(lang, doc);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const closeLabel = lang === 'bg' ? 'Затвори' : lang === 'de' ? 'Schließen' : lang === 'fr' ? 'Fermer'
    : lang === 'ru' ? 'Закрыть' : lang === 'uk' ? 'Закрити' : lang === 'it' ? 'Chiudi'
    : lang === 'es' ? 'Cerrar' : 'Close';
  const versionLabel = lang === 'bg' ? 'Версия' : lang === 'de' ? 'Fassung' : lang === 'fr' ? 'Version'
    : lang === 'ru' ? 'Версия' : lang === 'uk' ? 'Версія' : lang === 'it' ? 'Versione'
    : lang === 'es' ? 'Versión' : 'Version';

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        // Clicking the backdrop closes. The dialog element reports backdrop
        // clicks as clicks on itself, so compare the target.
        if (e.target === ref.current) onClose();
      }}
      aria-label={content.title}
      style={{
        width: 'min(760px, calc(100vw - 32px))',
        maxHeight: 'min(84vh, 900px)',
        padding: 0,
        border: '1px solid var(--wc-border-strong)',
        background: 'var(--wc-surface)',
        color: 'var(--wc-body)',
        boxShadow: '0 12px 48px rgba(38, 49, 58, 0.28)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }}>
        {/* Sticky header, so the title and the way out stay reachable while
            reading a long document. */}
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '16px 20px',
            borderBottom: '1px solid var(--wc-border)',
            background: 'var(--wc-surface-alt)',
          }}
        >
          <div>
            <h2 style={{ fontSize: 18, marginBottom: 2 }}>{content.title}</h2>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--wc-faint)' }}>
              {versionLabel} <span className="wc-num">{content.updated}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            style={{
              flex: 'none',
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--wc-border-strong)',
              background: 'var(--wc-surface)',
              color: 'var(--wc-muted)',
              fontSize: 17,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </header>

        <div style={{ overflowY: 'auto', padding: '18px 20px 22px' }}>
          <p style={{ marginTop: 0, fontSize: 14.5, lineHeight: 1.6 }}>{content.intro}</p>

          {content.sections.map((section, i) => (
            <section key={i} style={{ marginTop: 20 }}>
              <h3
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'var(--wc-blue-ink)',
                  marginBottom: 7,
                  paddingBottom: 5,
                  borderBottom: '1px solid var(--wc-border)',
                }}
              >
                {section.heading}
              </h3>
              {section.body.map((para, j) => (
                <p key={j} style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6 }}>
                  {para}
                </p>
              ))}
            </section>
          ))}

          <div
            style={{
              marginTop: 24,
              padding: '13px 15px',
              background: 'var(--wc-surface-alt)',
              border: '1px solid var(--wc-border)',
              fontSize: 12.5,
              lineHeight: 1.55,
              color: 'var(--wc-muted)',
            }}
          >
            <strong style={{ color: 'var(--wc-ink)' }}>{COMPANY.legalName}</strong>
            <div>{addressLine}</div>
            <div style={{ marginTop: 3 }}>
              {COMPANY.email} &middot; {COMPANY.phone}
            </div>
          </div>
        </div>

        <footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--wc-border)',
            background: 'var(--wc-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="wc-btn wc-btn-sm" onClick={onClose}>
            {closeLabel}
          </button>
        </footer>
      </div>

      <style>{`
        dialog::backdrop { background: rgba(26, 35, 42, 0.55); }
      `}</style>
    </dialog>
  );
}
